import { supabase } from '@/utils/supabase';

export interface PostClosingComment {
  id: string;
  encompass_loan_id: string;
  author_id: string;
  author_name: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface PostClosingConditionDocument {
  document_id: string;
  file_name: string;
  original_filename: string | null;
  source_file: string | null;
  created_at: string;
  loan_document_status: string | null;
  queue_id: string | null;
  queue_status: string | null;
  attempts: number | null;
  next_attempt_at: string | null;
  error_code: string | null;
  error_message: string | null;
  attachment_id: string | null;
  target_document_id: string | null;
  completed_at: string | null;
  fallback_error_code: string | null;
  fallback_error_message: string | null;
}

export interface PostClosingConditionUploadResult {
  success: boolean;
  documentId: string;
  queueId?: string;
  storagePath: string;
  fileName: string;
}

export type PostClosingLoanPurchasedEmailStatus =
  | 'sent'
  | 'already_sent'
  | 'not_earliest'
  | 'disabled'
  | 'no_recipients'
  | 'error';

export interface PostClosingLoanPurchasedEmailResult {
  success: boolean;
  status: PostClosingLoanPurchasedEmailStatus;
  reason?: string;
  resendId?: string;
}

export async function getPostClosingComments(encompassLoanId: string): Promise<PostClosingComment[]> {
  const { data, error } = await supabase.rpc('get_post_closing_comments', {
    p_encompass_loan_id: encompassLoanId
  });

  if (error) {
    throw new Error(error.message || 'Failed to load post-closing comments');
  }

  return (data || []) as PostClosingComment[];
}

export async function addPostClosingComment(encompassLoanId: string, content: string): Promise<PostClosingComment> {
  const { data, error } = await supabase.rpc('add_post_closing_comment', {
    p_encompass_loan_id: encompassLoanId,
    p_content: content
  });

  if (error) {
    throw new Error(error.message || 'Failed to add post-closing comment');
  }

  const [comment] = (data || []) as PostClosingComment[];
  if (!comment) {
    throw new Error('Post-closing comment was not returned');
  }

  return comment;
}

export async function getPostClosingConditionDocuments(encompassLoanId: string): Promise<PostClosingConditionDocument[]> {
  const { data, error } = await supabase.rpc('get_post_closing_condition_documents', {
    p_encompass_loan_id: encompassLoanId
  });

  if (error) {
    throw new Error(error.message || 'Failed to load post-closing condition documents');
  }

  return (data || []) as PostClosingConditionDocument[];
}

export async function uploadPostClosingCondition(params: {
  loanId: string;
  encompassLoanId: string;
  file: File;
}): Promise<PostClosingConditionUploadResult> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('loanId', params.loanId);
  formData.append('encompassLoanId', params.encompassLoanId);

  const { data, error } = await supabase.functions.invoke('upload-post-closing-condition', {
    body: formData
  });

  if (error) {
    throw new Error(error.message || 'Failed to upload post-closing condition');
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Failed to upload post-closing condition');
  }

  return data as PostClosingConditionUploadResult;
}

export async function sendPostClosingLoanPurchasedEmail(params: {
  encompassLoanId: string;
  loanDocumentId: string;
}): Promise<PostClosingLoanPurchasedEmailResult> {
  const { data, error } = await supabase.functions.invoke('send-post-closing-loan-purchased', {
    body: {
      encompassLoanId: params.encompassLoanId,
      loanDocumentId: params.loanDocumentId
    }
  });

  if (error) {
    throw new Error(error.message || 'Failed to send Loan Purchased email');
  }

  if (!data?.status) {
    throw new Error(data?.error || 'Loan Purchased email returned an invalid response');
  }

  return data as PostClosingLoanPurchasedEmailResult;
}
